import { BlockProps } from '@/components/Block/Block';
import EmojiText from '@/components/EmojiText/EmojiText';
import P from '@/components/Redactie/P/P';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import { useBlockRemoteUserData } from '@/hooks/useBlockRemoteUserData';
import SendIcon from '@/resources/CustomIcons/SendIcon';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import ListEditor from '@/components/Redactie/ListEditor/ListEditor';
import { UserMessageBlockViewerContainer } from '@/components/Block/BlockViewerContainer';

import styles from './PollBlock.module.sass';

const answerBgColors = [
    'bg-primary-text-lg-aa',
    'bg-secondary-text-lg-aa',
    'bg-violet-500',
    'bg-teal-600',
    'bg-emerald-600',
    'bg-cinnamon-text-lg-aa',
    'bg-chambray-text-lg-aa',
] as const;

const answerFgColors = [
    'text-primary-text-base-aa',
    'text-secondary-text-base-aa',
    'text-violet-700',
    'text-teal-700',
    'text-emerald-700',
    'text-cinnamon-text-base-aa',
    'text-chambray-text-base-aa',
] as const;

interface PollBlockData {
    question: string;
    answers: string[];
}

interface PollBlockLocalData {
    pickedAnswer: number | null;
}

const PollBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<PollBlockData>({
        question: data.question ?? 'Wat vind jij?',
        answers: data.answers ?? ['Ja', 'Nee'],
    });

    if (isEditable) {
        return (
            <PollBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                isEditable={isEditable}
                onEdited={onEdited}
            />
        );
    } else {
        return (
            <PollBlockViewer
                id={id}
                uuid={uuid}
                subjectId={subjectId}
                blockData={blockData}
                updatedAt={updatedAt}
                isEditorPreview={isEditorPreview}
                isEditable={isEditable}
                isAnimated={isAnimated}
                onFinished={onFinished}
            />
        );
    }
};

const PollBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    isEditable,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: PollBlockData;
    setBlockData: React.Dispatch<React.SetStateAction<PollBlockData>>;
    isEditable: boolean;
    onEdited?: BlockProps['onEdited'];
}) => {
    // Save changes to block data
    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(
                JSON.stringify({
                    ...blockData,
                    answers: blockData.answers.filter(
                        (answer) => !!answer && answer.length > 0,
                    ),
                }),
            ),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="overflow-hidden">
            <P
                isEditable={isEditable}
                onChange={(value) => {
                    setBlockData({
                        ...blockData,
                        question: value,
                    });
                }}
                placeholder="Pollvraag"
                value={blockData.question}
                rows={1}
            >
                {blockData.question}
            </P>
            <ListEditor<string>
                list={blockData.answers}
                onSaved={(list) => {
                    setBlockData({
                        ...blockData,
                        answers: list,
                    });
                }}
                createNewItem={() => ''}
                validateItem={(item) => ({
                    isValid: !!item && item.length > 0,
                    validationText: 'Voeg een tekst toe bij dit antwoord',
                })}
                validateList={(list) => ({
                    isValid: list.every((item) => !!item && item.length > 0),
                    validationText: 'Voeg een tekst toe bij ieder antwoord',
                })}
                minItems={2}
                maxItems={7}
                listDescription={blockData.question}
                listDisplayName="Reacties"
                listItemDisplayName="Reactie"
                listSummaryItemData={(item) => ({
                    displayName: item,
                    icon: undefined,
                })}
                listItemTemplate={PollBlockAnswerEditor}
            />
        </BlockEditorContainer>
    );
};

const PollBlockAnswerEditor = (
    index: number,
    item: string,
    setItem: (item: string) => void,
) => (
    <P
        isEditable
        maxLength={96}
        onChange={(value) => {
            setItem(value);
        }}
        placeholder={`Reactie ${index + 1}`}
        value={item}
        rows={1}
    />
);

const PollBlockViewer = ({
    id,
    uuid,
    subjectId,
    blockData,
    updatedAt,
    isEditorPreview,
    isEditable,
    isAnimated,
    onFinished,
}: {
    id: BlockProps['id'];
    uuid: BlockProps['uuid'];
    subjectId: BlockProps['subjectId'];
    blockData: PollBlockData;
    updatedAt: BlockProps['updatedAt'];
    isEditorPreview: BlockProps['isEditorPreview'];
    isEditable: BlockProps['isEditable'];
    isAnimated: BlockProps['isAnimated'];
    onFinished?: () => void;
}) => {
    const { userDataRemote, submitUserDataRemote } = useBlockRemoteUserData(id);
    const {
        userDataLocal,
        setUserDataLocal,
    } = useBlockLocalUserData<PollBlockLocalData>(
        subjectId,
        id,
        updatedAt,
        isEditorPreview,
        {
            pickedAnswer: null,
        },
    );

    // Go to next block when user has picked an answer
    useEffect(() => {
        if (isEditable)
            setUserDataLocal({
                ...userDataLocal,
                pickedAnswer: null,
            });
        if (userDataLocal.pickedAnswer === null) return;
        else onFinished?.();
    }, [isEditable, userDataRemote, userDataLocal.pickedAnswer]);

    // Get an array of percentages for each answer, or null if not submitted and loaded yet
    const answerPercentages = useMemo(() => {
        if (!userDataRemote?.summary || userDataRemote?.submitted_data === null)
            return null;
        return blockData.answers.map((_, index) => {
            const count = userDataRemote.summary.find(
                (item) => item.data === index,
            )?.count;
            // Calculate percentage of total count, if count is a number and not undefined
            if (typeof count === 'number') {
                return Math.round((count / userDataRemote.count) * 100);
            } else {
                return 0;
            }
        });
    }, [userDataRemote, blockData]);

    // Get all answers that are not empty
    const answers = blockData.answers.filter((answer) => answer);

    return (
        <UserMessageBlockViewerContainer
            label={blockData.question}
            icon={SendIcon}
            isCollapsed={userDataLocal.pickedAnswer !== null}
            isAnimated={isAnimated}
        >
            <ol
                className={`${styles['pollblock__options']} ${
                    userDataLocal.pickedAnswer !== null
                        ? styles['pollblock__options--picked']
                        : ''
                }`}
                aria-label={`Lijst van ${answers.length} keuzes voor de vraag: ${blockData.question}`}
            >
                {[...answers]
                    .sort((answer) => {
                        // If answer is picked, put it at the top
                        if (
                            answers.indexOf(answer) ===
                            userDataLocal.pickedAnswer
                        )
                            return -1;
                        return 0;
                    })
                    .map((answer, sortedIndex) => (
                        <li key={answer}>
                            <PollBlockAnswerButton
                                uuid={uuid}
                                answer={answer}
                                answerPercentage={
                                    answerPercentages?.[
                                        answers.indexOf(answer)
                                    ] ?? null
                                }
                                index={answers.indexOf(answer)}
                                pickedAnswer={userDataLocal.pickedAnswer}
                                setPickedAnswer={(index: number) => {
                                    setUserDataLocal({
                                        ...userDataLocal,
                                        pickedAnswer: index,
                                    });
                                }}
                                submitUserDataRemote={(value, options) => {
                                    return submitUserDataRemote(value, {
                                        ...options,
                                        optimisticData: {
                                            submitted_data: value,
                                            count: answers.length,
                                            summary: [
                                                {
                                                    data: sortedIndex,
                                                    count: 1,
                                                },
                                            ],
                                        },
                                    });
                                }}
                            />
                        </li>
                    ))}
            </ol>
        </UserMessageBlockViewerContainer>
    );
};

interface PollBlockAnswerButtonProps {
    uuid: string;
    answer: string;
    answerPercentage: number | null;
    index: number;
    pickedAnswer: number | null;
    setPickedAnswer: (index: number) => void;
    submitUserDataRemote: ReturnType<
        typeof useBlockRemoteUserData
    >['submitUserDataRemote'];
}

const PollBlockAnswerButton = ({
    uuid,
    answer,
    answerPercentage,
    index,
    pickedAnswer,
    setPickedAnswer,
    submitUserDataRemote,
}: PollBlockAnswerButtonProps) => {
    const shouldReduceMotion = useReducedMotion();

    return (
        <motion.button
            className={`${answerBgColors[index]} ${
                styles['pollblock__options__option']
            } ${
                pickedAnswer !== index
                    ? styles['pollblock__options__option--discarded']
                    : ''
            }  ${
                pickedAnswer === index
                    ? styles['pollblock__options__option--picked']
                    : ''
            }`}
            layoutId={
                shouldReduceMotion
                    ? undefined
                    : `${uuid}__pollblock__options__option__${index}`
            }
            layout="position"
            layoutDependency={pickedAnswer}
            aria-pressed={pickedAnswer === index}
            aria-disabled={pickedAnswer !== null}
            onClick={() => {
                if (pickedAnswer !== null) return;
                setPickedAnswer(index);
                submitUserDataRemote(index);
            }}
        >
            <span className={styles['pollblock__options__option__answer']}>
                <EmojiText>{answer}</EmojiText>
            </span>
            {pickedAnswer === index && (
                <span className="sr-only">(door jou gekozen)</span>
            )}
            {pickedAnswer !== null && typeof answerPercentage === 'number' && (
                <>
                    <motion.span
                        className={`${styles['pollblock__options__option__percentage']} ${answerFgColors[index]}`}
                        initial={{
                            scale: shouldReduceMotion ? 1 : 0,
                        }}
                        animate={{
                            scale: 1,
                            transition: {
                                delay: shouldReduceMotion
                                    ? 0
                                    : 0.5 + 0.2 * index,
                            },
                        }}
                    >
                        {answerPercentage}%
                    </motion.span>
                </>
            )}
            <div
                className={styles['pollblock__options__option__bar']}
                style={{
                    width:
                        pickedAnswer !== null &&
                        typeof answerPercentage === 'number'
                            ? `${answerPercentage}%`
                            : '0%',
                }}
            />
        </motion.button>
    );
};

export default PollBlock;
